import { ApplicationConfig, LOCALE_ID, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { Router, TitleStrategy, provideRouter, withDisabledInitialNavigation, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeSrLatn from '@angular/common/locales/sr-Latn';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from 'primeng/api';

import { routes } from './app.routes';

// Serbian (latin) locale so DecimalPipe/DatePipe render dot-grouped thousands + comma decimals
// app-wide, matching the design's Serbian numerics.
registerLocaleData(localeSrLatn);
import { EgProblemService, EgSlatePreset, provideEgLauncher, provideEgLauncherIdentity } from '@expertgroup/shell';
import { EG_ASSISTANT_COPY_EN, provideEgAssistantCopy } from '@expertgroup/assistant';
import { localeId, trenutniJezik } from '@/app/core/copy/language';
import { environment } from '@/environments/environment';
import { authConfig } from '@/app/core/auth/auth.config';
import { AuthService } from '@/app/core/auth/auth.service';
import { ProtaxTitleStrategy } from '@/app/core/brand/protax-title.strategy';
import { authInterceptor } from '@/app/core/http/auth.interceptor';
import { errorInterceptor } from '@/app/core/http/error.interceptor';
import { graphqlProvider } from '@/app/core/graphql/graphql.provider';
import { EG_ASSISTANT_PROBLEM_SOURCE, egAssistantInterceptor, provideEgAssistant, provideEgAssistantScreen } from '@expertgroup/assistant';
import { assistantConfig } from '@/app/core/auth/assistant.config';
import { injectProtaxScreenResolver } from '@/app/core/screens/assistant-screen';
import { protaxSuggestionsProvider } from '@/app/core/screens/assistant-suggestions';
import { protaxAssistantHostProviders } from '@/app/core/screens/assistant-host';
import { protaxSupportProviders } from '@/app/core/support/protax-support';

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        provideRouter(routes, withDisabledInitialNavigation(), withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' })),
        // Naslov taba ide kroz masku (shell 0.16): „Stranica · ProTax", osnova je ime proizvoda maske.
        { provide: TitleStrategy, useClass: ProtaxTitleStrategy },
        // ⚠️ egAssistantInterceptor ide PRVI, ali redosled ovde nije ono što ih razdvaja: svaki od njih
        // proverava `ownsRequest` i propušta tuđe zahteve netaknute. Redosled je tu samo da asistentski
        // zahtev ne prođe kroz dve provere kad prva već zna odgovor.
        //
        // ⚠⚠ Bibliotečki interceptor se registruje SAMO kad je asistent upaljen. On bezuslovno injektuje
        // `EgAssistantSession`, koja traži `EG_ASSISTANT_CONFIG` — a nju daje `provideEgAssistant` ispod. Dok
        // je stajao u nizu bezuslovno, svako okruženje sa praznim `assistantResource` (dakle lokalno I
        // produkcija) padalo je na NG0201 još u inicijalizatoru, pre nego što se išta iscrta.
        provideHttpClient(withFetch(), withInterceptors(assistantConfig ? [egAssistantInterceptor, authInterceptor, errorInterceptor] : [authInterceptor, errorInterceptor])),
        // Prazan `assistantResource` = asistent isključen; tada se ne registruje ni konfiguracija ni sesija.
        ...(assistantConfig ? [provideEgAssistant(assistantConfig)] : []),
        ...(assistantConfig ? protaxAssistantHostProviders : []),
        // Kontekst ekrana: registar ekrana prevodi adresu u `uiContext` (id, ime, putanja). Bez ovoga serveru
        // ide `uiContext: null`. Fabrika je lenja — izvršava se tek kad traka zatraži resolver, pa bezuslovna
        // registracija ne košta ništa kad je asistent isključen.
        provideEgAssistantScreen(injectProtaxScreenResolver),
        // Ponuđena pitanja po ekranu: nad ISTIM kontekstom ekrana, preračunata na svakoj navigaciji. Dijalozi
        // ne menjaju adresu, pa dok je dijalog otvoren nude se pitanja ekrana-domaćina — vidi
        // `assistant-suggestions.ts`. Lenja fabrika, kao resolver iznad.
        protaxSuggestionsProvider,
        // Signal problema: interceptor (4xx sa RFC 7807), Apollo `onError` i forma nepokretnosti prijavljuju u
        // `EgProblemService` ljuske; asistent čita isti servis kroz svoj token (crvena tačka na pokretaču, čip sa
        // predlogom koji se NE šalje sam). `useExisting`, ne nova instanca — jedan servis, jedno stanje.
        // ⚠️ Bezuslovno: `EgProblemService` je primitiv ljuske, ne asistenta, i prijavljuje se i kad je asistent
        // isključen; token asistent čita tek kad postoji.
        { provide: EG_ASSISTANT_PROBLEM_SOURCE, useExisting: EgProblemService },
        // Podrška (Ticketing) — @expertgroup/support: konfiguracija, GraphQL adapter nad `graphqlEndpoint` (token kači
        // authInterceptor), poll bez toast-a, izvor problema i jezik. Sve ProTax-ovo je u `core/support/protax-support.ts`.
        ...protaxSupportProviders(),
        providePrimeNG({
            theme: {
                preset: EgSlatePreset,
                options: {
                    darkModeSelector: '.app-dark',
                    cssLayer: { name: 'primeng', order: 'tailwind-base, primeng, tailwind-utilities' }
                }
            }
        }),
        // Launcher: current-user grants and registered UI clients select entries; these URLs are optional overrides.
        provideEgLauncher(environment.ecosystem),
        provideEgLauncherIdentity(() => {
            const auth = inject(AuthService);
            return { issuer: environment.issuer, accessToken: () => auth.getAccessToken() };
        }),
        authConfig,
        // After provideHttpClient: apollo-angular/http sends through HttpClient, so every operation picks up
        // authInterceptor and errorInterceptor. That is why the Apollo link chain carries no auth link.
        graphqlProvider,
        MessageService,
        ConfirmationService,
        // ⚠️ Lokal prati izabrani jezik: bez ovoga bi engleski tekst išao sa srpskim formatom datuma i
        // hiljada — pola prevoda, što je gore od nijednog.
        { provide: LOCALE_ID, useValue: localeId() },
        // Traka asistenta ima ugrađen engleski u biblioteci; domaćin samo bira koji rečnik važi.
        ...(trenutniJezik() === 'en' ? [provideEgAssistantCopy(EG_ASSISTANT_COPY_EN)] : []),
        // Startup ordering is load-bearing (see ADR 0001 / Identity.UI): checkAuth() must fully process the
        // OIDC auth-code callback BEFORE the router navigates, so route guards never race the callback nonce.
        provideAppInitializer(async () => {
            const auth = inject(AuthService);
            const router = inject(Router);
            await auth.checkAuth();
            router.initialNavigation();
        })
    ]
};
